import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Autocomplete,
  ButtonGroup,
  Tooltip,
  Chip,
  Box
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { getJobOrders } from "../API/jobOrderAPI";
import { getCacheClients } from "../API/clientAPI";
import ArticleIcon from "@mui/icons-material/Article";
import { styled } from "@mui/material/styles";
import BaseWrapper from "../../components/shared/BaseWrapper";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { fetchAndFilterJobOrders } from "../JobOrders/utils/JobOrderFilter";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

const getTodayInPH = () => {
  const now = new Date();
  const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  );
  localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  return localDate.toISOString().split("T")[0];
};

function ViewClientServiceReports() {
  const [jobOrders, setJobOrders] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [clientsCache, setClientsCache] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [isDateRange, setIsDateRange] = useState(false);
  // const [searchFilter, setSearchFilter] = useState({
  //   client: "",
  //   startDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth(),
  //       1, // First day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endDate: (() => {
  //     const now = new Date();
  //     const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
  //     const localDate = new Date(
  //       now.getFullYear(),
  //       now.getMonth() + 1,
  //       0, // Last day of the current month
  //       0,
  //       0,
  //       0,
  //       0
  //     );
  //     localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
  //     return localDate.toISOString().split("T")[0];
  //   })(),
  //   endorsementForm: "",
  // });

  const [searchFilter, setSearchFilter] = useState({
    client: "",
    startDate: getTodayInPH(),
    endDate: getTodayInPH(),
    endorsementForm: "",
  });

  const handleSingleDateChange = (value) => {
    setSearchFilter((prev) => ({
      ...prev,
      startDate: value,
      endDate: value,
    }));
  };

  const handleDateRangeToggle = () => {
    setIsDateRange((prev) => {
      const next = !prev;

      if (!next) {
        setSearchFilter((current) => ({
          ...current,
          endDate: current.startDate,
        }));
      }

      return next;
    });
  };

  // get local user data to be used for tech view filter
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const technicianId = parsedUser.technician_id || "";

  const [totalJobOrders, setTotalJobOrders] = useState(null)

  const navigate = useNavigate();

  const clientFilter = createFilterOptions({
    matchFrom: "any",                     // allow substring matches, not just prefix
    stringify: (option) => option?.name || "",
  });

  useEffect(() => {
    const fetchCacheData = async () => {
      try {
        setClientsLoading(true);
        
        // Fetch clients
        const clientsCache = await getCacheClients();

        setClientsCache(clientsCache || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchCacheData();
  }, []);

  const handleSearchButtonClick = async () => {
    try {
      setIsLoading(true);
      const { list, total } = await fetchAndFilterJobOrders({
        searchFilter,                // includes client + dates + endorsementForm
        status: [],                  // no status filter
        serviceReportStatus: ["Done"],
        billingRecordStatus: [],
        userRoles,                   // include + technicianId only if you *really* want scoping here
        technicianId,
        getJobOrdersApi: getJobOrders
      });

      // View-specific shaping (format times, choose receipt URL, keep form_type for routing)
      const formatTime = (time) => {
        if (!time) return "";
        const [hour, minute] = time.split(":").map(Number);
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${String(minute).padStart(2, "0")} ${period}`;
      };

      const transformed = list.map((job) => {
        return {
          id: job.id,
          job_date: job.job_date,
          start_time: formatTime(job.start_time),
          end_time: formatTime(job.end_time),
          technicians: job.technicians || [],
          job_types: Array.isArray(job.job_types) ? job.job_types : [],
          client: job.client || "",
          reports_url: job.reports_url || "",
          receipts_url: job.receipts_url || "",
          certificate_url: job.certificate_url || "",
          form_type: job.form_type || "",
        };
      });

      setJobOrders(transformed);
      setTotalJobOrders(total);
      setError(null);
    } catch (err) {
      setError(err.message || String(err));
      setErrorMessage("Failed to fetch Service Reports. Please try again.");
    } finally {
      setIsLoading(false);
    }  
  }

  const handleSearchClient = (event, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      client: value,
    }));
  };

  // map your form_type → URL segment
  const formTypeToSegment = {
    makati_gpc_form: "makati-gpc-form",
    qc_form: "qc-form",
    termite_control_form: "termite-control-form",
    transmittal_form: "transmittal-form",
  };

  // build the right route for view / edit based on form_type
  const getReportRoute = (job, action) => {
    const segment = formTypeToSegment[job.form_type] || "service-report";
    const currentRoute = "/view-client-service-reports"; // Current page route
    return `/${action}-${segment}/${job.id}?returnTo=${encodeURIComponent(currentRoute)}`;
  };

  return (

    <BaseWrapper
      titleIcon={<ArticleIcon />}
      title="Client Retention"
      subTitle="View Client Service Reports"
      subTitleRoute="/view-client-service-reports"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >

      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          alignItems="stretch"
          sx={{
            mb: 2,
            "& .MuiFormControl-root": {
              height: "100%",
            },
            "& .MuiOutlinedInput-root": {
              minHeight: 40,
            },
            "& .MuiButton-root.filter-action-btn": {
              minHeight: 40,
            },
          }}
        >
          <Grid item xs={12} md={6} lg={4}>
            <Autocomplete
              options={clientsCache}
              getOptionLabel={(client) => client?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, params) => clientFilter(options, params)}
              openOnFocus
              autoHighlight
              loading={clientsLoading}
              noOptionsText={clientsLoading ? "Loading..." : "No clients found"}
              value={searchFilter.client || null}
              onChange={(event, newValue) => handleSearchClient(event, newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{option.name}</span>
                    <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Name"
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error ? error : null}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
                height: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              {!isDateRange ? (
                <TextField
                  label="Job Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={searchFilter.startDate}
                  onChange={(e) => handleSingleDateChange(e.target.value)}
                />
              ) : (
                <>
                  <TextField
                    label="Start Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={searchFilter.startDate}
                    onChange={(e) =>
                      setSearchFilter((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                  />

                  <TextField
                    label="End Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={searchFilter.endDate}
                    inputProps={{
                      min: searchFilter.startDate || undefined,
                    }}
                    onChange={(e) =>
                      setSearchFilter((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                  />
                </>
              )}

              <Button
                className="filter-action-btn"
                variant={isDateRange ? "contained" : "outlined"}
                onClick={handleDateRangeToggle}
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: { xs: "100%", sm: 140 },
                  flexShrink: 0,
                }}
              >
                {isDateRange ? "Single Date" : "Date Range"}
              </Button>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={2}
            sx={{
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Button
              className="filter-action-btn"
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick}
              disabled={isLoading}
              fullWidth
              sx={{
                height: "100%",
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />
      {jobOrders.length > 0 && (
        <>
          <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
            <Grid item xs={2}>
              <Card
                sx={{
                  backgroundColor: '#1976D2',
                  color: '#FFF',
                  borderRadius: 1,
                  textAlign: 'center',
                  padding: '4px',  // Minimal padding for a compact look
                  boxShadow: 1
                }}
              >
                <CardContent sx={{ padding: '4px' }}>  {/* Reduced internal padding */}
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Total Job Orders
                  </Typography>
                  <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
                    {totalJobOrders}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>


          <Card variant="outlined" sx={{ padding: "20px" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Job Date",
                      "Start Time",
                      "End Time",
                      "Client",
                      "Job Type",
                      "Technicians",
                      "Reports",
                      "Receipts",
                      "Certificates",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          padding: "6px",
                          backgroundColor: "#f7f7f7"
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobOrders.map((job, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.job_date || ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.start_time || ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.end_time || ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.client || ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.job_types && job.job_types.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {job.job_types.map((type, i) => (
                              <Chip
                                key={i}
                                label={type.replace(/_/g, " ")}
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.technicians.length > 0
                          ? job.technicians.map((tech, i) => (
                            <div key={i}>
                              {`${tech.firstname} ${tech.lastname}`}
                            </div>
                          ))
                          : ""}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.reports_url ? (
                          <a
                            href={job.reports_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "none",
                            }}
                          >
                            View Report
                          </a>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.receipts_url ? (
                          <a
                            href={job.receipts_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "none",
                            }}
                          >
                            View Receipt
                          </a>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        {job.certificate_url ? (
                          <a
                            href={job.certificate_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "none",
                            }}
                          >
                            View Certificate
                          </a>
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
                        <ButtonGroup
                          variant="outlined"
                          color="primary"
                          aria-label="service report actions"
                          size="small"
                          sx={{
                            gap: 0, // Remove gap between buttons
                            padding: 0,
                          }}
                        >
                          <Tooltip title="View Service Report" arrow>
                            <span>
                              <Button
                                onClick={() => navigate(getReportRoute(job, "view"))}
                                startIcon={<VisibilityIcon />}
                                sx={{
                                  textTransform: "none",
                                  minWidth: "32px", // Ensure the button is compact
                                  padding: "6px",
                                  borderRadius: "4px",
                                }}
                              />
                            </span>
                          </Tooltip>

                          <Tooltip title="Edit Service Report" arrow>
                            <span>
                              <Button
                                onClick={() => navigate(getReportRoute(job, "edit"))}
                                startIcon={<EditIcon />}
                                sx={{
                                  textTransform: "none",
                                  minWidth: "32px", // Ensure the button is compact
                                  padding: "6px",
                                  borderRadius: "4px",
                                }}
                              />
                            </span>
                          </Tooltip>
                        </ButtonGroup>

                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          </Card>
        </>
      )}
    </BaseWrapper>
    // <ThemeProvider theme={theme}>
    //   {/* Success Snackbar */}
    //   <Snackbar
    //     open={!!successMessage}
    //     autoHideDuration={3000}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setSuccessMessage("")} // Clear the success message on close
    //   >
    //     <Alert
    //       severity="success"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {successMessage}
    //     </Alert>
    //   </Snackbar>

    //   {/* Loading Snackbar */}
    //   <Snackbar
    //     open={isLoading}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //   >
    //     <Alert
    //       severity="info"
    //       icon={<CircularProgress size={18} />}
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       Loading, please wait...
    //     </Alert>
    //   </Snackbar>

    //   {/* Error Snackbar */}
    //   <Snackbar
    //     open={!!errorMessage}
    //     anchorOrigin={{ vertical: "top", horizontal: "center" }}
    //     onClose={() => setErrorMessage("")}
    //   >
    //     <Alert
    //       severity="error"
    //       sx={{
    //         display: "flex",
    //         justifyContent: "center",
    //         alignItems: "center",
    //         width: "auto",
    //         maxWidth: "100%",
    //         mx: "auto",
    //       }}
    //     >
    //       {errorMessage}
    //     </Alert>
    //   </Snackbar>
    //   <Box sx={{ padding: "40px 60px" }}>
    //     <Card
    //       variant="outlined"
    //       style={{
    //         marginTop: "40px",
    //         marginBottom: "40px",
    //         borderRadius: 0,
    //       }}
    //     >
    //       <CardHeader
    //         title={
    //           <Box
    //             display="flex"
    //             alignItems="center"
    //             justifyContent="space-between"
    //           >
    //             <Box display="flex" alignItems="center">
    //               <ArticleIcon
    //                 sx={{
    //                   fontSize: "40px",
    //                   marginRight: "8px",
    //                 }}
    //               />
    //               <Typography variant="h6">Client Retention</Typography>
    //             </Box>
    //             <Breadcrumbs
    //               aria-label="breadcrumb"
    //               separator={<NavigateNextIcon fontSize="small" />}
    //               sx={{ color: "white" }}
    //             >
    //               <Typography sx={{ color: "#ffffff99" }}>
    //                 Client Retention
    //               </Typography>
    //               <Typography
    //                 component={Link}
    //                 to="/manage-technicians"
    //                 sx={{ color: "#ffffff", textDecoration: "none" }}
    //               >
    //                 View Client Service Reports
    //               </Typography>
    //             </Breadcrumbs>
    //           </Box>
    //         }
    //         sx={{
    //           backgroundColor: "#1e5d45",
    //           color: "white",
    //           padding: "16px",
    //         }}
    //       />
    //       <CardContent sx={{ padding: "20px" }}>
    //         <Card variant="outlined" sx={{ padding: "20px" }}>
    //           <CardHeader
    //             title={
    //               <Typography variant="h5" sx={{ fontWeight: "bold" }}>
    //                 Client Service Reports
    //               </Typography>
    //             }
    //             sx={{ marginBottom: 2 }}
    //           />
    //           <Grid item xs={12} sm={12}>
    //             <Grid container spacing={3} sx={{ mb: 2 }} alignItems="center">
    //               <Grid item xs={12} sm={3} md={4}>
    //                 <Autocomplete
    //                   freeSolo
    //                   options={clientsCache}
    //                   getOptionLabel={(client) => client?.name || ""}
    //                   value={searchFilter.client || null}
    //                   onChange={(event, newValue) =>
    //                     handleSearchClient(event, newValue)
    //                   }
    //                   renderInput={(params) => (
    //                     <TextField
    //                       {...params}
    //                       label="Client Name"
    //                       variant="outlined"
    //                       fullWidth
    //                       error={!!error}
    //                       helperText={error || ""}
    //                     />
    //                   )}
    //                 />
    //               </Grid>
    //               <Grid item xs={12} sm={2} md={2}>
    //                 <TextField
    //                   label="Start Date"
    //                   type="date"
    //                   variant="outlined"
    //                   fullWidth
    //                   InputLabelProps={{
    //                     shrink: true,
    //                   }}
    //                   value={searchFilter.startDate}
    //                   onChange={(e) =>
    //                     setSearchFilter((prev) => ({
    //                       ...prev,
    //                       startDate: e.target.value,
    //                     }))
    //                   }
    //                 />
    //               </Grid>
    //               <Grid item xs={12} sm={2} md={2}>
    //                 <TextField
    //                   label="End Date"
    //                   type="date"
    //                   variant="outlined"
    //                   fullWidth
    //                   InputLabelProps={{
    //                     shrink: true,
    //                   }}
    //                   value={searchFilter.endDate}
    //                   onChange={(e) =>
    //                     setSearchFilter((prev) => ({
    //                       ...prev,
    //                       endDate: e.target.value,
    //                     }))
    //                   }
    //                 />
    //               </Grid>

    //               <Grid
    //                 item
    //                 xs={12}
    //                 sm={12}
    //                 md={2}
    //                 container
    //               // justifyContent="flex-end"
    //               >
    //                 <Button
    //                   variant="contained"
    //                   color="primary"
    //                   onClick={handleSearchButtonClick}
    //                 // disabled={loading}
    //                 >
    //                   {loading ? "Search..." : "Search"}
    //                 </Button>
    //               </Grid>
    //             </Grid>
    //           </Grid>
    //           <StyledHr />
    //           {jobOrders.length > 0 && (
    //             <>
    //               <Grid item xs={12} container spacing={1} sx={{ marginBottom: 1 }}>
    //                 <Grid item xs={2}>
    //                   <Card
    //                     sx={{
    //                       backgroundColor: '#1976D2',
    //                       color: '#FFF',
    //                       borderRadius: 1,
    //                       textAlign: 'center',
    //                       padding: '4px',  // Minimal padding for a compact look
    //                       boxShadow: 1
    //                     }}
    //                   >
    //                     <CardContent sx={{ padding: '4px' }}>  {/* Reduced internal padding */}
    //                       <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
    //                         Total Job Orders
    //                       </Typography>
    //                       <Typography variant="h5" sx={{ fontSize: '1.5rem' }}>
    //                         {totalJobOrders}
    //                       </Typography>
    //                     </CardContent>
    //                   </Card>
    //                 </Grid>
    //               </Grid>


    //               <Card variant="outlined" sx={{ padding: "20px" }}>
    //                 <TableContainer>
    //                   <Table size="small">
    //                     <TableHead>
    //                       <TableRow>
    //                         {[
    //                           "Job Date",
    //                           "Start Time",
    //                           "End Time",
    //                           "Client",
    //                           "Job Scope",
    //                           "Technicians",
    //                           "Reports",
    //                           "Receipts",
    //                           "Certificates",
    //                           "Action",
    //                         ].map((header) => (
    //                           <TableCell
    //                             key={header}
    //                             sx={{
    //                               fontSize: "0.8rem",
    //                               fontWeight: "bold",
    //                               padding: "6px",
    //                               backgroundColor: "#f7f7f7"
    //                             }}
    //                           >
    //                             {header}
    //                           </TableCell>
    //                         ))}
    //                       </TableRow>
    //                     </TableHead>
    //                     <TableBody>
    //                       {jobOrders.map((job, index) => (
    //                         <TableRow key={index}>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.job_date || ""}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.start_time || ""}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.end_time || ""}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.client || ""}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.job_scope.length > 0 ? (
    //                               <ol
    //                                 style={{
    //                                   margin: "0",
    //                                   padding: "0",
    //                                   listStylePosition: "inside",
    //                                 }}
    //                               >
    //                                 {job.job_scope.map((scope, i) => (
    //                                   <li key={i}>
    //                                     <b>{scope.competency}</b>
    //                                     {scope.specialization.length > 0
    //                                       ? `: ${scope.specialization.join(", ")}`
    //                                       : ""}
    //                                   </li>
    //                                 ))}
    //                               </ol>
    //                             ) : (
    //                               ""
    //                             )}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.technicians.length > 0
    //                               ? job.technicians.map((tech, i) => (
    //                                 <div key={i}>
    //                                   {`${tech.firstname} ${tech.lastname}`}
    //                                 </div>
    //                               ))
    //                               : ""}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.reports_url ? (
    //                               <a
    //                                 href={job.reports_url}
    //                                 target="_blank"
    //                                 rel="noopener noreferrer"
    //                                 style={{
    //                                   color: "#1976d2",
    //                                   textDecoration: "none",
    //                                 }}
    //                               >
    //                                 View Report
    //                               </a>
    //                             ) : (
    //                               ""
    //                             )}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.receipts_url ? (
    //                               <a
    //                                 href={job.receipts_url}
    //                                 target="_blank"
    //                                 rel="noopener noreferrer"
    //                                 style={{
    //                                   color: "#1976d2",
    //                                   textDecoration: "none",
    //                                 }}
    //                               >
    //                                 View Receipt
    //                               </a>
    //                             ) : (
    //                               ""
    //                             )}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             {job.certificate_url ? (
    //                               <a
    //                                 href={job.certificate_url}
    //                                 target="_blank"
    //                                 rel="noopener noreferrer"
    //                                 style={{
    //                                   color: "#1976d2",
    //                                   textDecoration: "none",
    //                                 }}
    //                               >
    //                                 View Certificate
    //                               </a>
    //                             ) : (
    //                               ""
    //                             )}
    //                           </TableCell>
    //                           <TableCell sx={{ fontSize: "0.75rem", padding: "6px" }}>
    //                             <Box display="flex" alignItems="center">
    //                               <Button
    //                                 variant="contained"
    //                                 color="primary"
    //                                 onClick={() =>
    //                                   navigate(`/view-service-report/${job.id}`)
    //                                 }
    //                                 sx={{
    //                                   marginRight: "10px",
    //                                   fontSize: "0.75rem",
    //                                   padding: "4px 10px",
    //                                 }}
    //                               >
    //                                 View
    //                               </Button>
    //                               <Button
    //                                 variant="contained"
    //                                 color="primary"
    //                                 onClick={() =>
    //                                   navigate(`/edit-service-report/${job.id}`)
    //                                 }
    //                                 sx={{
    //                                   fontSize: "0.75rem",
    //                                   padding: "4px 10px",
    //                                 }}
    //                               >
    //                                 Edit
    //                               </Button>
    //                             </Box>
    //                           </TableCell>
    //                         </TableRow>
    //                       ))}
    //                     </TableBody>
    //                   </Table>
    //                 </TableContainer>

    //               </Card>
    //             </>
    //           )}
    //         </Card>
    //       </CardContent>
    //     </Card>
    //   </Box>
    // </ThemeProvider>
  );
}

export default ViewClientServiceReports;
